"use client"

import { FIELD_LABEL, FIELD_MONO, FIELD_UNDERLINE } from "@/components/graphite/field"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

interface TimePickerProps {
  value?: string // Format: "HH:MM" in 24-hour format
  onChange: (time: string) => void
  label?: string
  id?: string
  /**
   * What this picker sets — "Start", "End". A form holds two of these under
   * headings that name them, but a `FieldShell` heading is type rather than a
   * `<label for>`, so without this both read out as "Hour", "Minute" and
   * "AM or PM" and nothing says which time is being changed.
   */
  name?: string
}

export function TimePicker({ value, onChange, label, id, name }: TimePickerProps) {
  // Parse the 24-hour time value into hour, minute, and period
  const parseTime = (time?: string) => {
    if (!time) return { hour: "", minute: "", period: "AM" }

    const [hourStr, minuteStr] = time.split(":")
    const hour24 = Number.parseInt(hourStr, 10)
    const minute = minuteStr || "00"

    const period = hour24 >= 12 ? "PM" : "AM"
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24

    return {
      hour: hour12.toString(),
      minute,
      period,
    }
  }

  // Convert 12-hour format to 24-hour format
  const formatTime = (hour: string, minute: string, period: string) => {
    if (!hour || !minute) return ""

    let hour24 = Number.parseInt(hour, 10)
    if (period === "PM" && hour24 !== 12) {
      hour24 += 12
    } else if (period === "AM" && hour24 === 12) {
      hour24 = 0
    }

    return `${hour24.toString().padStart(2, "0")}:${minute}`
  }

  const { hour, minute, period } = parseTime(value)

  const handleHourChange = (newHour: string) => {
    const newTime = formatTime(newHour, minute || "00", period)
    onChange(newTime)
  }

  const handleMinuteChange = (newMinute: string) => {
    const newTime = formatTime(hour || "12", newMinute, period)
    onChange(newTime)
  }

  const handlePeriodChange = (newPeriod: string) => {
    const newTime = formatTime(hour || "12", minute || "00", newPeriod)
    onChange(newTime)
  }

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString())
  const minutes = ["00", "15", "30", "45"]

  /*
    Three underlines that read as one clock, not three boxes.

    The structure stays a picker — hour, minute, meridiem — because a free-text
    time has to be parsed and can be wrong, and the minutes are the quarters a
    desk actually books. Only the skin changes, so it stops being the one boxed
    control on a form of canvas 14e underlines.
  */
  const called = (part: string) => (name ? `${name} ${part.toLowerCase()}` : part)

  const trigger = cn(
    FIELD_UNDERLINE,
    FIELD_MONO,
    "h-auto flex-none gap-1.5 pb-[7px] [&>svg]:size-3 [&>svg]:opacity-60",
  )

  return (
    <div>
      {label && <div className={FIELD_LABEL}>{label}</div>}
      <div className={cn("flex items-baseline gap-2.5", label && "mt-[9px]")}>
        <Select value={hour} onValueChange={handleHourChange}>
          <SelectTrigger id={id} aria-label={called("Hour")} className={cn(trigger, "w-[58px]")}>
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {hours.map((h) => (
              <SelectItem key={h} value={h}>
                {h}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={minute} onValueChange={handleMinuteChange}>
          <SelectTrigger aria-label={called("Minute")} className={cn(trigger, "w-[62px]")}>
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            {minutes.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger aria-label={called("AM or PM")} className={cn(trigger, "w-[64px]")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
